import { SdkContext } from '../internal'

/** F-199: one account erasure request (`MemberErasure` in the API spec). Never carries the cancel link. */
export interface MemberErasure {
  memberId: string
  /** `self`: the person (30 days to give up); `team`: owner or admin, at the member's request (no way back). */
  source: 'self' | 'team'
  requestedAt: string
  /** The effective date: the later of the 30-day window and the end of the period already paid. */
  scheduledAt: string
  /** End of the paid period that kept the access (and pushed the date), when there is one. */
  paidUntil: string | null
  canceledAt: string | null
  erasedAt: string | null
}

/** F-200: the latest personal data export of the person here, and when a new one is accepted. */
export interface PersonalDataExportStatus {
  latest: {
    exportId: string
    status: 'queued' | 'ready' | 'failed' | 'refused' | 'expired'
    /** `team`: owner or admin generated it at the person's request. */
    requestedBy: 'self' | 'team'
    size: number | null
    createdAt: string
    /** Until when the file exists (72 h after it was built). */
    expiresAt: string | null
  } | null
  canRequest: boolean
  nextAvailableAt: string | null
}

/** `GET /api/privacy/exports/{id}/download`: a signed link that lives five minutes. */
export interface PersonalDataExportLink {
  url: string
  fileName?: string
  size?: number | null
  urlExpiresAt?: string
  expiresAt?: string
}

/** F-199 (erasure) and F-200 (export) under `/api/privacy`; the community is always `X-CommunityId`. */
export class PrivacyResource {
  constructor(private ctx: SdkContext) {}

  /** The person's pending request in this community, or `null`. */
  async getErasure(): Promise<MemberErasure | null> {
    return this.ctx.call<MemberErasure | null>({ httpMethod: 'GET', path: '/privacy/erasure' })
  }

  /**
   * Asks to erase the person's own account, with the password. 400 `erasure.ownerBlocked` (owner),
   * 400 on `password` (wrong, or an account without one: the team path).
   */
  async requestErasure(password: string, reason?: string): Promise<MemberErasure> {
    return this.ctx.call<MemberErasure>(
      { httpMethod: 'POST', path: '/privacy/erasure', hasBody: true },
      { body: { password, ...(reason?.trim() ? { reason: reason.trim() } : {}) } }
    )
  }

  /** Gives up, signed in. 404 nothing pending, 409 a team request, 410 already erased. */
  async cancelErasure(): Promise<MemberErasure> {
    return this.ctx.call<MemberErasure>({ httpMethod: 'DELETE', path: '/privacy/erasure' })
  }

  /** Gives up with the token of the e-mail's "Desistir" link, no session. The e-mail + password way is `auth.cancelErasureWithLogin`. */
  async cancelErasureByToken(token: string): Promise<MemberErasure> {
    return this.ctx.call<MemberErasure>(
      { httpMethod: 'POST', path: '/privacy/erasure/cancel', hasBody: true },
      { body: { token } }
    )
  }

  /** Owner and admin, over a lower role, reason required (3–1000): no withdrawal period, not cancelable. */
  async eraseMember(memberId: string, reason: string): Promise<MemberErasure> {
    return this.ctx.call<MemberErasure>(
      { httpMethod: 'POST', path: '/privacy/members/{memberId}/erasure', hasBody: true },
      { memberId, body: { reason } }
    )
  }

  /** F-200: the person's latest export and `canRequest` / `nextAvailableAt`. */
  async getExport(): Promise<PersonalDataExportStatus> {
    return this.ctx.call<PersonalDataExportStatus>({ httpMethod: 'GET', path: '/privacy/exports' })
  }

  /** F-200: queues the export (202), with the password. 409 one in progress, 429 `data.nextAvailableAt`. */
  async requestExport(password: string): Promise<PersonalDataExportStatus> {
    return this.ctx.call<PersonalDataExportStatus>(
      { httpMethod: 'POST', path: '/privacy/exports', hasBody: true },
      { body: { password } }
    )
  }

  /** F-200: only the person whose data it holds; 404 not theirs, 409 not ready, 410 expired. */
  async downloadExport(exportId: string): Promise<PersonalDataExportLink> {
    return this.ctx.call<PersonalDataExportLink>({ httpMethod: 'GET', path: '/privacy/exports/{exportId}/download' }, { exportId })
  }

  /** F-200: owner and admin, over a lower role, reason 3–1000; the link goes to the member's e-mail. */
  async exportMember(memberId: string, reason: string): Promise<PersonalDataExportStatus> {
    return this.ctx.call<PersonalDataExportStatus>(
      { httpMethod: 'POST', path: '/privacy/members/{memberId}/exports', hasBody: true },
      { memberId, body: { reason } }
    )
  }
}
